/**
 * 10.9IH — COMO JOGAR (o manual). Thiago: "explicar como funciona cada parte do jogo".
 *
 * O tour guiado (`GuideTour`) aponta o dedo pra tela e é de propósito CURTO — ninguém aguenta 20
 * cartões seguidos antes de jogar. Mas "cada parte do jogo" é muito mais do que cabe num tour: é
 * Ameaça, forja, tiers, profundidade, classes, bosses. Então o resto mora aqui, num manual que
 * o jogador abre quando quiser, pela pausa, e lê na ordem que quiser.
 *
 * Só leitura, sanfona (uma seção aberta por vez) pra caber em tela de celular, e com um botão pra
 * refazer o tour guiado.
 */
import { i18n } from "../i18n/i18n";

/** Ordem de leitura: do que o jogador precisa nos 5 primeiros minutos até o fim de jogo. */
const SECTIONS: { id: string; icon: string }[] = [
  { id: "combat", icon: "⚔️" },
  { id: "base", icon: "🛡️" },
  { id: "chain", icon: "⛓️" },
  { id: "death", icon: "💀" },
  { id: "threat", icon: "📈" },
  { id: "loot", icon: "🎁" },
  { id: "forge", icon: "🔨" },
  { id: "money", icon: "🪙" },
  { id: "tree", icon: "✦" },
  { id: "classes", icon: "🎭" },
  { id: "world", icon: "🗺️" },
  { id: "missions", icon: "📜" },
  { id: "bosses", icon: "☠️" },
  { id: "endgame", icon: "🌀" },
];

export interface HowToPlayHandlers {
  onClose: () => void;
  /** Refazer o tour guiado (fecha o manual e começa o tour). */
  onReplayTour: () => void;
}

export class HowToPlay {
  private readonly root: HTMLDivElement;
  private open_ = "";

  constructor(uiRoot: HTMLElement, private readonly h: HowToPlayHandlers) {
    this.root = document.createElement("div");
    this.root.className = "overlay htp-overlay";
    this.root.hidden = true;
    uiRoot.appendChild(this.root);
    this.root.addEventListener("click", (e) => {
      const el = e.target as HTMLElement;
      if (el.closest('[data-a="close"]')) {
        this.hide();
        this.h.onClose();
        return;
      }
      if (el.closest('[data-a="replay"]')) {
        this.hide();
        this.h.onReplayTour();
        return;
      }
      const head = el.closest<HTMLElement>(".htp-head");
      if (head?.dataset.id) {
        // sanfona: tocar na seção aberta fecha; tocar em outra troca
        this.open_ = this.open_ === head.dataset.id ? "" : head.dataset.id;
        this.render();
      }
    });
    i18n.onChange(() => {
      if (!this.root.hidden) this.render();
    });
  }

  show(): void {
    this.open_ = SECTIONS[0].id; // abre a primeira: o manual nunca aparece "vazio"
    this.render();
    this.root.hidden = false;
  }

  hide(): void {
    this.root.hidden = true;
  }

  private render(): void {
    const esc = (t: string): string => t.replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" })[c]!);
    // O corpo de cada seção é UM texto com quebras de linha; cada linha vira um parágrafo.
    const body = (id: string): string =>
      i18n
        .t(`htp.${id}.b`)
        .split("\n")
        .filter((l) => l.trim().length > 0)
        .map((l) => `<p>${esc(l.trim())}</p>`)
        .join("");
    const rows = SECTIONS.map((s) => {
      const aberto = this.open_ === s.id;
      return `<div class="htp-sec${aberto ? " open" : ""}">
        <button class="htp-head" data-id="${s.id}">
          <span class="htp-ic">${s.icon}</span>
          <span class="htp-t">${esc(i18n.t(`htp.${s.id}.t`))}</span>
          <span class="htp-caret">${aberto ? "▾" : "▸"}</span>
        </button>
        ${aberto ? `<div class="htp-body">${body(s.id)}</div>` : ""}
      </div>`;
    }).join("");
    this.root.innerHTML = `<div class="panel htp-panel">
      <button class="close-x" data-a="close">✕</button>
      <h2>❓ ${esc(i18n.t("htp.title"))}</h2>
      <p class="htp-sub">${esc(i18n.t("htp.sub"))}</p>
      <button class="btn primary htp-replay" data-a="replay">▶ ${esc(i18n.t("htp.replay"))}</button>
      <div class="htp-list">${rows}</div>
    </div>`;
  }
}
